import { FC, useEffect, useState } from "react";

import { DataGrid, GridColDef } from "@mui/x-data-grid";
import { Paper, Typography } from "@mui/material";
import { dataTables } from "src/styles";
import { ImageViewerModal } from "./modal.image-viewer";

import { ProductVariant } from "../types/api";

import _ from "lodash";

interface Props {
  loading?: boolean;
  variants: ProductVariant[];
  onRowClick: (row: any) => void;
}

const StocktakeVariantTable: FC<Props> = (props: Props) => {
  const [rows, setRows] = useState<any[]>();
  const [imageModalOpen, setImageModalOpen] = useState(false);
  const [selectedImage, setSelectedImage] = useState({ url: "", alt: "" });

  const handleImageClick = (imageUrl: string, imageAlt: string, event: React.MouseEvent) => {
    event.stopPropagation(); // Prevent row click
    setSelectedImage({ url: imageUrl, alt: imageAlt });
    setImageModalOpen(true);
  };

  const handleCloseModal = () => {
    setImageModalOpen(false);
  };

  useEffect(() => {
    const newRows = props.variants.filter((v: any) => v.stockOnHand > 0).map((pv: ProductVariant) => {
      const p = pv.product;
      
      const _fixedUri = pv.imageUrls?.[0] || "https://upload.wikimedia.org/wikipedia/commons/1/14/No_Image_Available.jpg";
      
      const row = {
        id: pv.guid || `${p.guid}-${pv.sku}`,
        image: [_fixedUri, pv.name],
        sku: pv.sku,
        name: pv.filterValues ? `${p.name} (${pv.filterValues})` : p.name,
        barcode: pv.barcode,
        stockOnHand: pv.stockOnHand
      };

      return row;
    });

    // console.log(newRows);
    setRows(newRows);
  }, [props.variants]);

  const columns: GridColDef[] = [
    { field: "id" },
    {
      field: "image",
      headerName: "Image",
      sortable: false,
      minWidth: 80,
      align: "center",
      renderCell: (params) => (
        <img
          src={params.value[0]}
          height={40}
          width={40}
          alt={params.value[1]}
          onClick={(e) => handleImageClick(params.value[0], params.value[1], e)}
          style={{ cursor: "pointer" }}
          onError={(e) => {
            (e.target as HTMLImageElement).src = "https://upload.wikimedia.org/wikipedia/commons/1/14/No_Image_Available.jpg";
          }}
        />
      ),
    },
    {
      field: "sku",
      headerName: "SKU",
      flex: 1,
      filterable: false,
    },
    {
      field: "name",
      headerName: "Name",
      minWidth: 400,
      flex: 1,
      filterable: false,
    },
    {
      field: "barcode",
      headerName: "Barcode",
      flex: 1,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
    },
    {
      field: "stockOnHand",
      headerName: "Stock on Hand",
      align: "center",
      flex: 1,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
    },
  ];

  return (
    <>
      <Paper
        component="form"
        sx={{ p: "2px 4px", display: "flex", alignItems: "center" }}
      >
        {rows && (
          <DataGrid
            localeText={{noRowsLabel:"Well done! Everything in this location has been scanned!"}}
            loading={props.loading}
            rows={rows}
            columns={columns}
            autoHeight
            density="compact"
            disableRowSelectionOnClick
            columnVisibilityModel={{
              id: false
            }}
            sx={dataTables}
            onRowClick={(r) => props.onRowClick(props.variants.find((v) => v.sku === r.row.sku))}
          />
        )}
      </Paper>
      <ImageViewerModal
        open={imageModalOpen}
        imageUrl={selectedImage.url}
        imageAlt={selectedImage.alt}
        onClose={handleCloseModal}
      />
    </>
  );
};

export default StocktakeVariantTable;
